import { PageWrapper as IntroRoomPage } from "./introduction-room";
import { Page as WaitingRoomPage } from "./waiting-room";
import { Page as MeetingRoomPage } from "./meeting-room";
import { PageWrapper as RejoinPage } from "./rejoin-page/Page";
import { useAtomValue } from "jotai";
import {
  VisitorState,
  shouldShowStaleVersionToastAtom,
  visitorStateAtom,
} from "./state";
import { useInitVisitorGlobalDDLoggerContext } from "./useInitVisitorGlobalDDLoggerContext";
import { useInitVisitorUserTracking } from "./useInitVisitorUserTracking";
import {
  useDetectStaleVersionInterval,
  useAnnouncementToast,
} from "../components";
import { AppState, UserType } from "../types";
import { useParams } from "react-router-dom";
import { useGetPublicPatient } from "../useGetPublicPatientByShortId";
import { useGetClientUserPaperwork } from "./measures/useGetClientUserPaperwork";
import { useGetClientSafetyGuard } from "./waiting-room/pre-session-prompt/useGetClientSafetyGuard";
import { Error404Page } from "../error/404Page";
import {
  blockedTelehealthPatientShortIds,
  blockInvalidUrlsGate,
} from "../statsig/gates";
import { useDynamicConfig, useFeatureGate } from "@statsig/react-bindings";
import { useIsValidProviderAndPatient } from "../hooks/useIsValidProviderAndPatient";

function visitorStateToAppState(
  visitorState: VisitorState,
): AppState | undefined {
  switch (visitorState) {
    case VisitorState.TIMEOUT:
      return AppState.TIMEOUT;
    case VisitorState.TAB_AWAY:
      return AppState.TAB_AWAY;
    case VisitorState.LOADING:
    case VisitorState.WAITING:
      return AppState.WAITING;
    case VisitorState.MEETING:
      return AppState.MEETING;
    case VisitorState.INTRO:
      return AppState.INTRO;
    default:
      return undefined;
  }
}

function MeasuresPageEffect() {
  useGetClientUserPaperwork();
  useGetClientSafetyGuard();
  return null;
}

function PageEffects({ visitorState }: { visitorState: VisitorState }) {
  const { patientShortId } = useParams<{
    patientShortId: string;
  }>();
  useInitVisitorGlobalDDLoggerContext();
  useInitVisitorUserTracking();
  useDetectStaleVersionInterval(useAtomValue(shouldShowStaleVersionToastAtom));
  useAnnouncementToast({
    userType: UserType.CLIENT,
    appState: visitorStateToAppState(visitorState),
    userId: patientShortId,
  });
  useGetPublicPatient(patientShortId);

  return <MeasuresPageEffect />;
}

function PageContent({ visitorState }: { visitorState: VisitorState }) {
  switch (visitorState) {
    case VisitorState.WAITING:
    case VisitorState.LOADING:
      return <WaitingRoomPage />;
    case VisitorState.MEETING:
      return <MeetingRoomPage />;
    case VisitorState.TIMEOUT:
    case VisitorState.TAB_AWAY:
      return <RejoinPage />;
    default:
      return <IntroRoomPage />;
  }
}

export function Page() {
  const visitorState = useAtomValue(visitorStateAtom);
  const { providerShortId, patientShortId } = useParams<{
    providerShortId: string;
    patientShortId: string;
  }>();

  const { value: blockInvalidUrlFeature } =
    useFeatureGate(blockInvalidUrlsGate);

  const { data, loading } = useIsValidProviderAndPatient(
    providerShortId!,
    patientShortId!,
  );

  const isInvalidUrl =
    blockInvalidUrlFeature && !loading && !data?.isValidProviderAndPatient;

  const blockedPatientShortIdSet = new Set(
    useDynamicConfig(blockedTelehealthPatientShortIds)?.get<string[]>(
      "patientShortIds",
    ) ?? [],
  );

  const shouldBlockEntry =
    blockedPatientShortIdSet.has(patientShortId) || isInvalidUrl;

  /**
   * This does not fully prevent access to a Provider's waiting room,
   * since having the providerShortId and patientShortId alone is enough to join.
   *
   * However, we only distribute session links containing both the
   * providerShortId and patientShortId. This check blocks any patientShortId
   * we've explicitly chosen to deny, which should prevent most unwanted access.
   */

  if (shouldBlockEntry) return <Error404Page />;

  return (
    <>
      <PageEffects visitorState={visitorState} />
      <PageContent visitorState={visitorState} />
    </>
  );
}
